import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaUsers, FaUserSlash, FaSignOutAlt, FaFont, FaPalette, FaUndo, FaUpload } from "react-icons/fa";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from 'react-bootstrap/Container';
import env from "react-dotenv";
import getUserId from "../utils/getUserId";

const UPLOAD_URL = `${env.API_URL}/addDime`;

const DEFAULT_FONTS = [
  "Poppins, Arial, sans-serif",
  "Montserrat, Arial, sans-serif",
  "Roboto, Arial, sans-serif",
  "Oswald, Arial, sans-serif",
  "Playfair Display, serif",
  "Bebas Neue, sans-serif",
  "Impact, sans-serif",
];

const CATEGORIES = [
  "Other",
  "Adventure & Travel",
  "Animals",
  "Art",
  "Beauty",
  "Electronics",
  "Events",
  "Fashion",
  "Food & Drink",
  "Home Decor",
  "Vehicles"
];

export default function AddDime() {
  // Load user ID from localStorage on component mount
  const [userId, setUserId] = React.useState('');
  
  React.useEffect(() => {
    // Use the robust getUserId utility
    const id = getUserId();
    
    if (id) {
      setUserId(id);
      console.log('AddDime: User ID loaded successfully:', id);
    } else {
      console.error('AddDime: No user ID available');
    }
  }, []);

  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [category, setCategory] = useState("Other");

  const [overlayText, setOverlayText] = useState("");
  const [textColor, setTextColor] = useState("#ffffff");
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONTS[0]);
  const [fontSize, setFontSize] = useState(36);

  const [filterPreset, setFilterPreset] = useState("none");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);

  const [pos, setPos] = useState({ x: 50, y: 50 });

  const [strokeColor, setStrokeColor] = useState("rgba(0,0,0,0.9)");
  const [strokeWidth, setStrokeWidth] = useState(4);

  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);
  const imageRef = useRef(null);
  const offscreenCanvasRef = useRef(null);

  const getCssFilter = useCallback(() => {
    const preset = {
      none: "",
      grayscale: "grayscale(100%)",
      sepia: "sepia(100%)",
      vintage: "sepia(50%) contrast(120%) brightness(90%)",
      cool: "hue-rotate(180deg) saturate(150%)",
      warm: "sepia(30%) saturate(150%)",
      blur: "blur(3px)",
      invert: "invert(100%)",
    }[filterPreset] || "";

    return `${preset} brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
  }, [filterPreset, brightness, contrast, saturation]);

  const handleImageUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageDataUrl(ev.target.result);
      setPreviewKey((k) => k + 1);
      setPos({ x: 50, y: 50 });
    };
    reader.readAsDataURL(f);
  };

  const resetEditor = () => {
    setOverlayText("");
    setTextColor("#ffffff");
    setFontFamily(DEFAULT_FONTS[0]);
    setFontSize(36);
    setFilterPreset("none");
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setPos({ x: 50, y: 50 });
    setStrokeColor("rgba(0,0,0,0.9)");
    setStrokeWidth(4);
  };

  const quickPosition = (which) => {
    if (which === "top") setPos({ x: pos.x, y: 10 });
    if (which === "middle") setPos({ x: pos.x, y: 50 });
    if (which === "bottom") setPos({ x: pos.x, y: 90 });
  };

  // Manual drag behavior
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    e.preventDefault();
    isDragging.current = true;

    const rect = previewContainerRef.current.getBoundingClientRect();

    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;

    dragOffset.current = {
      x: clientX - (rect.left + (pos.x / 100) * rect.width),
      y: clientY - (rect.top + (pos.y / 100) * rect.height),
    };
  };

  const onDragMove = (e) => {
    if (!isDragging.current) return;

    const rect = previewContainerRef.current.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;

    const x = clientX - rect.left - dragOffset.current.x;
    const y = clientY - rect.top - dragOffset.current.y;

    const xPct = Math.min(100, Math.max(0, (x / rect.width) * 100));
    const yPct = Math.min(100, Math.max(0, (y / rect.height) * 100));

    setPos({ x: xPct, y: yPct });
  };

  const stopDrag = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", onDragMove);
    window.addEventListener("touchend", stopDrag);

    return () => {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", onDragMove);
      window.removeEventListener("touchend", stopDrag);
    };
  }, []);

  const renderFinalImageDataUrl = async () => {
    if (!imageDataUrl) return null;

    if (!offscreenCanvasRef.current) offscreenCanvasRef.current = document.createElement("canvas");
    const canvas = offscreenCanvasRef.current;
    const imgEl = imageRef.current;
    if (!imgEl) return null;

    canvas.width = imgEl.naturalWidth;
    canvas.height = imgEl.naturalHeight;
    const ctx = canvas.getContext("2d");

    // Apply filters and draw image
    ctx.filter = getCssFilter();
    ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
    ctx.filter = "none";

    // Draw text if it exists
    if (overlayText && overlayText.trim()) {
      // Calculate exact position on canvas
      const x = (pos.x / 100) * canvas.width;
      const y = (pos.y / 100) * canvas.height;

      // Scale font proportionally
      const scaleFactor = canvas.width / 600; // 600px is our preview width baseline
      const scaledFontSize = Math.max(16, Math.round(fontSize * scaleFactor));

      // Set font properties
      ctx.font = `bold ${scaledFontSize}px ${fontFamily.split(",")[0]}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      
      // Draw stroke/outline if enabled
      if (strokeWidth > 0) {
        const scaledStrokeWidth = Math.max(1, strokeWidth * scaleFactor);
        ctx.lineWidth = scaledStrokeWidth;
        ctx.strokeStyle = strokeColor;
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.strokeText(overlayText, x, y);
      }
      
      // Draw filled text
      ctx.fillStyle = textColor;
      ctx.fillText(overlayText, x, y);
    }

    return canvas.toDataURL("image/png");
  };

  const handleSubmit = async () => {
    if (!imageDataUrl) {
      alert("Please select an image first!");
      return;
    }

    if (!category) {
      alert("Please select a category!");
      return;
    }

    // Get user ID using the utility (tries multiple sources)
    const finalUserId = userId || getUserId();
    
    if (!finalUserId) {
      alert("Error: User not logged in. Please sign in again.");
      return;
    }

    const dataUrl = await renderFinalImageDataUrl();
    if (!dataUrl) {
      alert("Error rendering image.");
      return;
    }

    const blob = await (await fetch(dataUrl)).blob();
    const formData = new FormData();
    formData.append("profile_image", blob, "dime.png");
    formData.append("category", category);

    console.log('Submitting dime for user_id:', finalUserId);

    try {
      const resp = await fetch(UPLOAD_URL, { method: "POST", credentials: 'include', body: formData });
      if (resp.ok) {
        alert("Dime added successfully!");
        window.location.href = "/userdimes";
      } else {
        alert("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Upload failed. Please try again.");
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      {/* Modern Navigation */}
      <Navbar expand="lg" fixed="top" bg="white" variant="light" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Container fluid style={{ maxWidth: '1800px' }}>
          <Navbar.Brand href="/home" style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
            <img src="/images/p10.jpg" alt="P10" style={{ height: '32px', width: '32px', marginRight: '8px',borderRadius: '8px' }}/>
            <span style={{ fontWeight: '600', fontSize: '1.2rem', color: '#1f2937' }}>Add Dime</span>
          </Navbar.Brand>
          
          <Navbar.Toggle aria-controls="main-navbar" />
          <Navbar.Collapse id="main-navbar">
            <Nav className="ms-auto">
              <Link to="/home" className="nav-link" style={{ fontWeight: '600' }}>
                <FaUsers style={{ marginRight: '6px' }}/> Home
              </Link>
              <Link to="/userdimes" className="nav-link" style={{ fontWeight: '600' }}>
                <FaUsers style={{ marginRight: '6px' }}/> My Dimes
              </Link>
              <Link to="/deleteProfile" className="nav-link" style={{ fontWeight: '600' }}>
                <FaUserSlash style={{ marginRight: '6px' }}/> Delete Account
              </Link>
              <Link to="/signin" className="nav-link" style={{ fontWeight: '600', color: '#e60023' }}>
                <FaSignOutAlt style={{ marginRight: '6px' }}/> Log out
              </Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div style={{ maxWidth: 1200, margin: "100px auto 36px", padding: "0 20px" }}>
        <h1 style={{ textAlign: "center", fontSize: '2.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '10px' }}>
          Create Your Dime
        </h1>
        <h2 style={{ textAlign: "center", fontSize: '1.2rem', color: '#6b7280', marginBottom: '40px' }}>
          Upload, enhance, and add text to your image
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 360px", gap: 24 }}>
          {/* PREVIEW */}
          <div>
            <div
              ref={previewContainerRef}
              style={previewBox}
            >
              {imageDataUrl ? (
                <>
                  <img
                    key={previewKey}
                    ref={imageRef}
                    src={imageDataUrl}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      filter: getCssFilter()
                    }}
                  />

                  {/* MOVABLE TEXT */}
                  {overlayText && (
                    <div
                      onMouseDown={startDrag}
                      onTouchStart={startDrag}
                      style={{
                        position: "absolute",
                        left: `${pos.x}%`,
                        top: `${pos.y}%`,
                        transform: "translate(-50%, -50%)",
                        fontFamily,
                        fontSize,
                        color: textColor,
                        cursor: "grab",
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        WebkitTextStroke: strokeWidth > 0 ? `${strokeWidth}px ${strokeColor}` : 'none',
                        textShadow: strokeWidth > 0 ? `0 2px 4px ${strokeColor}` : 'none',
                        userSelect: 'none',
                        pointerEvents: 'auto'
                      }}
                    >
                      {overlayText}
                    </div>
                  )}
                </>
              ) : (
                <div style={placeholder}>
                  <div style={{ textAlign: 'center' }}>
                    <FaUpload style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '16px' }} />
                    <p style={{ fontSize: '1.2rem', color: '#6b7280' }}>No Image Selected</p>
                    <p style={{ fontSize: '0.9rem', color: '#9ca3af' }}>Click "Choose Image" to get started</p>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <button style={btnBlue} onClick={() => fileInputRef.current.click()}>
                <FaUpload /> Choose Image
              </button>
              <button style={btnGray} onClick={resetEditor}>
                <FaUndo /> Reset All
              </button>
              <button style={{...btnGreen, flex: 2}} onClick={handleSubmit}>
                <FaUpload /> Add Dime
              </button>
            </div>
          </div>

          {/* RIGHT CONTROLS */}
          <div style={rightBox}>
            <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>Customize Your Dime</h3>

            {/* Category Selection */}
            <label className="label" style={labelStyle}>📁 Category *</label>
            <select 
              style={input} 
              value={category} 
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>

            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

            {/* Text Overlay */}
            <label className="label" style={labelStyle}><FaFont /> Text Overlay</label>
            <input
              value={overlayText}
              onChange={(e) => setOverlayText(e.target.value)}
              placeholder="Add text to your image..."
              style={input}
            />

            <label className="label" style={labelStyle}>Font Family</label>
            <select style={input} value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
              {DEFAULT_FONTS.map(f => <option key={f}>{f}</option>)}
            </select>

            <label className="label" style={labelStyle}>Font Size: {fontSize}px</label>
            <input 
              type="range" 
              min="14" 
              max="120" 
              value={fontSize} 
              onChange={(e) => setFontSize(+e.target.value)}
              style={rangeStyle}
            />

            <label className="label" style={labelStyle}>Text Color</label>
            <input 
              type="color" 
              value={textColor} 
              onChange={(e) => setTextColor(e.target.value)}
              style={{...input, height: '50px', cursor: 'pointer'}}
            />

            <label className="label" style={labelStyle}>Text Position</label>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btnSmall} onClick={() => quickPosition("top")}>Top</button>
              <button style={btnSmall} onClick={() => quickPosition("middle")}>Middle</button>
              <button style={btnSmall} onClick={() => quickPosition("bottom")}>Bottom</button>
            </div>

            <label className="label" style={labelStyle}>Outline Width: {strokeWidth}px</label>
            <input 
              type="range" 
              min="0" 
              max="12" 
              value={strokeWidth} 
              onChange={(e) => setStrokeWidth(+e.target.value)}
              style={rangeStyle}
            />

            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

            {/* Image Filters */}
            <label className="label" style={labelStyle}><FaPalette /> Image Filters</label>
            <select style={input} value={filterPreset} onChange={(e) => setFilterPreset(e.target.value)}>
              <option value="none">None</option>
              <option value="grayscale">Grayscale</option>
              <option value="sepia">Sepia</option>
              <option value="vintage">Vintage</option>
              <option value="cool">Cool</option>
              <option value="warm">Warm</option>
              <option value="blur">Blur</option>
              <option value="invert">Invert</option>
            </select>

            <label className="label" style={labelStyle}>Brightness: {brightness}%</label>
            <input 
              type="range" 
              min="0" 
              max="200" 
              value={brightness} 
              onChange={(e) => setBrightness(+e.target.value)}
              style={rangeStyle}
            />

            <label className="label" style={labelStyle}>Contrast: {contrast}%</label>
            <input 
              type="range" 
              min="0" 
              max="200" 
              value={contrast} 
              onChange={(e) => setContrast(+e.target.value)}
              style={rangeStyle}
            />

            <label className="label" style={labelStyle}>Saturation: {saturation}%</label>
            <input 
              type="range" 
              min="0" 
              max="200" 
              value={saturation} 
              onChange={(e) => setSaturation(+e.target.value)}
              style={rangeStyle}
            />

            <button style={{...btnGreen, width: '100%', marginTop: '20px'}} onClick={handleSubmit}>
              <FaUpload /> Add Dime
            </button>
          </div>
        </div>
      </div>

      {/* Modern Footer */}
      <div style={{ 
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '20px 0',
        marginTop: '60px',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.05)'
      }}>
        <Container>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
            fontSize: '0.9rem'
          }}>
            <span style={{ color: '#6b7280' }}>© 2025 p10rating</span>
            <Link to="/privacypolicy" style={{ color: '#3b82f6', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/disclaimer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Disclaimer</Link>
            <Link to="/terms" style={{ color: '#3b82f6', textDecoration: 'none' }}>Terms</Link>
            <Link to="/about" style={{ color: '#3b82f6', textDecoration: 'none' }}>About</Link>
          </div>
        </Container>
      </div>
    </div>
  );
}

const previewBox = {
  width: "100%",
  height: 600,
  borderRadius: 12,
  overflow: "hidden",
  position: "relative",
  background: "#111827",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
};

const placeholder = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#e5e7eb",
};

const input = {
  width: "100%",
  padding: 10,
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 12,
  fontSize: '14px'
};

const labelStyle = {
  display: 'block',
  fontWeight: '600',
  marginBottom: '6px',
  color: '#374151',
  fontSize: '14px'
};

const rangeStyle = {
  width: '100%',
  marginBottom: '12px',
  cursor: 'pointer'
};

const btnBlue = {
  flex: 1,
  background: "#3b82f6",
  color: "white",
  padding: "12px 16px",
  borderRadius: 10,
  border: "none",
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const btnGreen = {
  flex: 1,
  background: "#10b981",
  color: "white",
  padding: "12px 16px",
  borderRadius: 10,
  border: "none",
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const btnGray = {
  background: "#6b7280",
  color: "white",
  padding: "12px 16px",
  borderRadius: 10,
  border: "none",
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const btnSmall = {
  flex: 1,
  background: "#3b82f6",
  color: "white",
  padding: "8px 12px",
  borderRadius: 8,
  border: "none",
  fontWeight: 600,
  fontSize: '13px',
  cursor: 'pointer'
};

const rightBox = {
  background: "white",
  padding: 24,
  borderRadius: 12,
  boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
  maxHeight: '90vh',
  overflowY: 'auto'
};